import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import FormInput from "../../../components/controls/FormInput";
import {
  BuildingOfficeIcon,
  GlobeAltIcon,
  IdentificationIcon,
  PhoneIcon,
  UserCircleIcon,
} from "@heroicons/react/20/solid";
import Autocomplete from "../../../components/Places/PlacesAutocomplete";
import ImageUploader from "../../../components/controls/ImageUploader";
import { PlusIcon } from "@heroicons/react/24/outline";
import UserRolesSection from "./UserRolesSection";

export default function AdminUserModal({
  isOpen,
  onClose,
  onSave,
  user,
  availableRoles = [],
  currentUserId = "",
}) {
  const { t } = useTranslation();
  const isEditing = !!user;

  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [isProvider, setIsProvider] = useState(false);
  const [isGoogleAccount, setIsGoogleAccount] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [activeCompanyIndex, setActiveCompanyIndex] = useState(0);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [initialRoleIds, setInitialRoleIds] = useState([]);

  useEffect(() => {
    if (user) {
      setEmail(user.email || "");
      setFullName(user.fullName || "");
      setIsProvider(user.isProvider || false);
      setIsGoogleAccount(user?.isGoogleAccount || false);
      setCompanies(user.companies || []);
      setSelectedRoleIds(user.userRoles?.map((r) => r.roleId) || []);
      if (user?.userRoles) {
        const ids = user.userRoles.map((r) => r.roleId);
        setSelectedRoleIds(ids);
        setInitialRoleIds(ids);
      }
    } else {
      setEmail("");
      setFullName("");
      setIsProvider(false);
      setIsGoogleAccount(false);
      setCompanies([]);
      setSelectedRoleIds([]);
    }
  }, [user]);

  const handleClose = () => {
    setEmail("");
    setFullName("");
    setIsProvider(false);
    setIsGoogleAccount(false);
    setCompanies([]);
    onClose();
  };

  const addCompany = () => {
    const newCompany = {
      name: "",
      neqNumber: "",
      address: "",
      phoneNumber: "",
      contactPerson: "",
      website: "",
      contactEmail: "",
      imageUrl: "",
      subscriptionTier: 1,
      userId: user?.id || null,
      location: null,
      tempImageUrl: "",
      isActive: true,
    };
    setCompanies([...companies, newCompany]);
    setActiveCompanyIndex(companies.length);
  };

  const handleSave = async () => {
    const roleDiff = selectedRoleIds
      .filter((id) => !initialRoleIds.includes(id))
      .concat(initialRoleIds.filter((id) => !selectedRoleIds.includes(id)));

    if (roleDiff.length > 0) {
      const changedDescriptions = availableRoles
        .filter((r) => roleDiff.includes(r.id))
        .map((r) => r.description || r.name);

      const confirmText =
        "Les rôles suivants ont été modifiés, enregistrer ?\n\n- " +
        changedDescriptions.join("\n- ");

      const confirmed = window.confirm(confirmText);
      if (!confirmed) return;
    }

    const payload = {
      id: user?.id || null,
      email,
      fullName,
      isProvider,
      isGoogleAccount,
      companies: companies.map((company) => ({
        ...company,
        imageUrl: company.hasChangedImageUrl
          ? company.tempImageUrl ?? null
          : company.imageUrl,
      })),
      userRoles: selectedRoleIds.map((roleId) => ({
        roleId,
        userId: user?.id || null,
      })),
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/User`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-User-Id": currentUserId || "",
          },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        const errorText = await response.text();

        if (errorText.includes("UserAlreadyExists")) {
          alert("Un utilisateur existe déjà avec cet email.");
        } else {
          alert("Une erreur est survenue : " + errorText);
        }

        throw new Error(errorText); // pour bloquer plus loin si besoin
      }

      onClose();
      onSave(payload);
    } catch (error) {}
  };

  const companyIsActiveToggle = (activeCompanyIndex) => (
    <div className="form-control">
      <label className="cursor-pointer label flex items-center gap-4">
        <span
          className={`label-text font-semibold ${
            companies[activeCompanyIndex].isActive
              ? "text-green-600"
              : "text-red-500"
          }`}
        >
          {companies[activeCompanyIndex].isActive
            ? t("adminUserTable.companyActive")
            : t("adminUserTable.companyInactive")}
        </span>
        <input
          type="checkbox"
          className={`toggle ${
            companies[activeCompanyIndex].isActive
              ? "toggle-success"
              : "toggle-error"
          }`}
          checked={companies[activeCompanyIndex].isActive}
          onChange={(e) => {
            const newValue = e.target.checked;
            if (!newValue) {
              const confirmed = window.confirm(
                t("adminUserTable.confirmDeactivate")
              );
              if (!confirmed) return;
            }

            const updated = [...companies];
            updated[activeCompanyIndex].isActive = newValue;
            setCompanies(updated);
          }}
        />
      </label>
    </div>
  );
  return (
    <dialog className={`modal ${isOpen ? "modal-open" : ""}`}>
      <div className="modal-box w-full max-w-7xl">
        <h3 className="font-bold text-lg mb-4">
          {isEditing
            ? t("adminUserTable.editTitle")
            : t("adminUserTable.addTitle")}
        </h3>

        <div className="tabs tabs-lifted border p-2">
          <input
            type="radio"
            name="user-tabs"
            className="tab [&:checked]:bg-base-200 [&:checked]:font-bold"
            aria-label={t("adminUserTable.generalTab")}
            defaultChecked
          />
          <div className="tab-content border-base-300 bg-base-100 p-4 space-y-4">
            <FormInput
              label={t("adminUserTable.email")}
              name="email"
              value={email}
              onChange={(name, value) => setEmail(value)}
              required
            />
            <FormInput
              label={t("adminUserTable.fullName")}
              name="fullName"
              value={fullName}
              onChange={(name, value) => setFullName(value)}
            />
            <div className="form-control hidden">
              <label className="cursor-pointer label">
                <span className="label-text">
                  {t("adminUserTable.isProvider")}
                </span>
                <input
                  type="checkbox"
                  className="toggle"
                  checked={isProvider}
                  onChange={() => setIsProvider(!isProvider)}
                />
              </label>
            </div>
            <div className="form-control">
              <label className="cursor-pointer label">
                <span className="label-text">Compte Google ?</span>
                <input
                  type="checkbox"
                  className="toggle"
                  checked={isGoogleAccount}
                  onChange={(e) => setIsGoogleAccount(e.target.checked)}
                />
              </label>
            </div>
          </div>

          <input
            type="radio"
            name="user-tabs"
            className="tab [&:checked]:bg-base-200 [&:checked]:font-bold"
            aria-label={t("adminUserTable.companyTab")}
          />

          <div className="tab-content border-base-300 bg-base-100 p-4">
            <div className="mb-4">
              <button
                type="button"
                className="btn btn-sm rounded-full bg-primary text-white hover:bg-accent"
                onClick={addCompany}
              >
                <PlusIcon className="w-5 h-5"></PlusIcon>{" "}
                {t("adminUserTable.addCompany")}
              </button>
            </div>

            {companies.length >= 1 && (
              <div className="tabs tabs-lifted w-full mb-4">
                {companies.map((c, index) => (
                  <a
                    key={index}
                    className={`tab ${
                      activeCompanyIndex === index
                        ? "tab-active bg-gray-300 rounded-lg"
                        : ""
                    }`}
                    onClick={() => setActiveCompanyIndex(index)}
                  >
                    {c.name?.trim() || t("adminUserTable.newCompany")}
                  </a>
                ))}
              </div>
            )}
            {companies.length === 0 ? (
              <p className="text-center text-gray-500 italic">
                {t("adminUserTable.noCompany")}
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2  border p-4">
                <div className="col-span-1 sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <FormInput
                    label={t("becomeProvider.companyNameLabel")}
                    value={companies[activeCompanyIndex].name}
                    placeholder={t("becomeProvider.companyNamePlaceholder")}
                    onChange={(name, value) => {
                      const updated = [...companies];
                      updated[activeCompanyIndex].name = value;
                      setCompanies(updated);
                    }}
                    required
                    icon={
                      <BuildingOfficeIcon className="w-5 h-5 text-gray-500" />
                    }
                  />

                  <Autocomplete
                    onSelect={(e) => {
                      const updated = [...companies];
                      updated[activeCompanyIndex].address = e.formattedAddress;
                      updated[activeCompanyIndex].location = e;
                      setCompanies(updated);
                    }}
                    initialAddress={companies[activeCompanyIndex].address || ""}
                  />
                  <FormInput
                    label={t("becomeProvider.contactEmailLabel")}
                    value={companies[activeCompanyIndex].contactEmail || ""}
                    placeholder={t("becomeProvider.contactEmailPlaceholder")}
                    onChange={(name, value) => {
                      const updated = [...companies];
                      updated[activeCompanyIndex].contactEmail = value;
                      setCompanies(updated);
                    }}
                    required
                  />
                  <FormInput
                    label={t("becomeProvider.phoneNumberLabel")}
                    value={companies[activeCompanyIndex].phoneNumber}
                    placeholder={t("becomeProvider.phoneNumberPlaceholder")}
                    onChange={(name, value) => {
                      const updated = [...companies];
                      updated[activeCompanyIndex].phoneNumber = value;
                      setCompanies(updated);
                    }}
                    icon={<PhoneIcon className="w-5 h-5 text-gray-500" />}
                    required
                  />
                  <FormInput
                    label={t("becomeProvider.contactPersonLabel")}
                    value={companies[activeCompanyIndex].contactPerson}
                    placeholder={t("becomeProvider.contactPersonPlaceholder")}
                    onChange={(name, value) => {
                      const updated = [...companies];
                      updated[activeCompanyIndex].contactPerson = value;
                      setCompanies(updated);
                    }}
                    icon={<UserCircleIcon className="w-5 h-5 text-gray-500" />}
                    required
                  />
                  <FormInput
                    label={t("becomeProvider.neqNumberLabel")}
                    value={companies[activeCompanyIndex].neqNumber}
                    placeholder={t("becomeProvider.neqNumberPlaceholder")}
                    onChange={(name, value) => {
                      const updated = [...companies];
                      updated[activeCompanyIndex].neqNumber = value;
                      setCompanies(updated);
                    }}
                    icon={
                      <IdentificationIcon className="w-5 h-5 text-gray-500" />
                    }
                  />
                  <FormInput
                    label={t("becomeProvider.websiteLabel")}
                    value={companies[activeCompanyIndex].website}
                    placeholder={t("becomeProvider.websitePlaceholder")}
                    onChange={(name, value) => {
                      const updated = [...companies];
                      updated[activeCompanyIndex].website = value;
                      setCompanies(updated);
                    }}
                    icon={<GlobeAltIcon className="w-5 h-5 text-gray-500" />}
                  />
                </div>
                <div>
                  <ImageUploader
                    initialImageUrl={
                      companies[activeCompanyIndex]?.tempImageUrl ||
                      companies[activeCompanyIndex]?.imageUrl ||
                      ""
                    }
                    onTempImageChange={(url) => {
                      const updated = [...companies];
                      updated[activeCompanyIndex].tempImageUrl = url?.trim()
                        ? url
                        : null;
                      updated[activeCompanyIndex].hasChangedImageUrl = true;
                      setCompanies(updated);
                    }}
                  />
                </div>
                {companyIsActiveToggle(activeCompanyIndex)}
              </div>
            )}
          </div>
          <input
            type="radio"
            name="user-tabs"
            className="tab [&:checked]:bg-base-200 [&:checked]:font-bold"
            aria-label={t("Roles")}
          />
          <div className="tab-content border-base-300 bg-base-100 p-4 space-y-4 min-h-[400px]">
            <UserRolesSection
              values={selectedRoleIds}
              onChange={setSelectedRoleIds}
              availableRoles={availableRoles}
            />
          </div>
        </div>

        <div className="modal-action mt-6">
          <button
            className="btn rounded-full btn-outline"
            onClick={handleClose}
          >
            {t("adminUserTable.cancel")}
          </button>
          <button
            className="btn rounded-full bg-primary text-white hover:bg-accent"
            onClick={handleSave}
          >
            {t("adminUserTable.save")}
          </button>
        </div>
      </div>
    </dialog>
  );
}
